import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  marketplaceOperatingBrainApi,
  type AcceptanceCriterion,
  type MOBRecommendation,
  type APISpecification,
  type BusinessObjective,
  type CoreResponsibility,
  type DatabaseTable,
  type EngineDashboard,
  type EngineValidation,
  type MOBPRDStatus,
  type IntelligenceInput,
  type DecisionField,
  type PrioritizationFactor,
  type ConversationExample,
  type AutonomousCapability,
  type ExecutiveBrief,
  type LearningTarget,
  type PerformanceTarget,
  type TestingRequirement,
  type UXCapability,
} from '../../api/marketplace-operating-brain-prd';

type Tab =
  | 'overview'
  | 'responsibilities'
  | 'inputs'
  | 'decisions'
  | 'prioritization'
  | 'conversation'
  | 'autonomous'
  | 'executive'
  | 'learning'
  | 'recommendations'
  | 'ux'
  | 'engineering'
  | 'status';
type Theme = 'light' | 'dark';

const riskColors: Record<string, string> = {
  Low: 'bg-rose-100 text-rose-800 dark:bg-rose-900 dark:text-rose-200',
  Medium: 'bg-fuchsia-100 text-fuchsia-800 dark:bg-fuchsia-900 dark:text-fuchsia-200',
  High: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
};

export function MarketplaceOperatingBrainPRDPage() {
  const [theme, setTheme] = useState<Theme>('light');
  const dark = theme === 'dark';
  const [tab, setTab] = useState<Tab>('overview');
  const [dashboard, setDashboard] = useState<EngineDashboard | null>(null);
  const [objectives, setObjectives] = useState<BusinessObjective[]>([]);
  const [responsibilities, setResponsibilities] = useState<CoreResponsibility[]>([]);
  const [inputs, setInputs] = useState<IntelligenceInput[]>([]);
  const [decisionFields, setDecisionFields] = useState<DecisionField[]>([]);
  const [prioritization, setPrioritization] = useState<PrioritizationFactor[]>([]);
  const [conversation, setConversation] = useState<ConversationExample[]>([]);
  const [autonomous, setAutonomous] = useState<AutonomousCapability[]>([]);
  const [executive, setExecutive] = useState<ExecutiveBrief[]>([]);
  const [learning, setLearning] = useState<LearningTarget[]>([]);
  const [recommendations, setRecommendations] = useState<MOBRecommendation[]>([]);
  const [uxCaps, setUxCaps] = useState<UXCapability[]>([]);
  const [perf, setPerf] = useState<PerformanceTarget[]>([]);
  const [apis, setApis] = useState<APISpecification[]>([]);
  const [tables, setTables] = useState<DatabaseTable[]>([]);
  const [testing, setTesting] = useState<TestingRequirement[]>([]);
  const [acceptance, setAcceptance] = useState<AcceptanceCriterion[]>([]);
  const [status, setStatus] = useState<MOBPRDStatus | null>(null);
  const [validation, setValidation] = useState<EngineValidation | null>(null);
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const results = await Promise.all([
        marketplaceOperatingBrainApi.dashboard(),
        marketplaceOperatingBrainApi.businessObjectives(),
        marketplaceOperatingBrainApi.coreResponsibilities(),
        marketplaceOperatingBrainApi.intelligenceInputs(),
        marketplaceOperatingBrainApi.decisionEngine(),
        marketplaceOperatingBrainApi.opportunityPrioritization(),
        marketplaceOperatingBrainApi.aiConversation(),
        marketplaceOperatingBrainApi.autonomousAssistance(),
        marketplaceOperatingBrainApi.executiveAdvisor(),
        marketplaceOperatingBrainApi.learningIntegration(),
        marketplaceOperatingBrainApi.recommendations(),
        marketplaceOperatingBrainApi.uxCapabilities(),
        marketplaceOperatingBrainApi.performanceTargets(),
        marketplaceOperatingBrainApi.apis(),
        marketplaceOperatingBrainApi.databaseSchema(),
        marketplaceOperatingBrainApi.testing(),
        marketplaceOperatingBrainApi.acceptanceCriteria(),
        marketplaceOperatingBrainApi.status(),
      ]);
      setDashboard(results[0]);
      setObjectives(results[1]);
      setResponsibilities(results[2]);
      setInputs(results[3]);
      setDecisionFields(results[4]);
      setPrioritization(results[5]);
      setConversation(results[6]);
      setAutonomous(results[7]);
      setExecutive(results[8]);
      setLearning(results[9]);
      setRecommendations(results[10]);
      setUxCaps(results[11]);
      setPerf(results[12]);
      setApis(results[13]);
      setTables(results[14]);
      setTesting(results[15]);
      setAcceptance(results[16]);
      setStatus(results[17]);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load Marketplace Operating Brain PRD');
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const validate = async () => {
    setBusy(true);
    setActionMsg(null);
    try {
      const result = await marketplaceOperatingBrainApi.validate({
        attrs: {
          context_ready: true,
          decision_engine_ready: true,
          prioritization_ready: true,
          conversation_ready: true,
          autonomous_ready: true,
          executive_ready: true,
          learning_ready: true,
          performance_met: true,
          acceptance_passed: true,
        },
      });
      setValidation(result);
      setActionMsg(
        result.valid
          ? 'Marketplace Operating Brain PRD passes production readiness gate'
          : `Gaps: ${result.gaps?.join(', ')}`,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Validation failed');
    } finally {
      setBusy(false);
    }
  };

  const card = dark ? 'border-rose-900/40 bg-slate-900' : 'border-rose-200 bg-white';
  const muted = dark ? 'text-slate-400' : 'text-slate-500';
  const shell = dark ? 'bg-slate-950 text-slate-100' : 'bg-rose-50/30 text-slate-900';
  const accent = dark ? 'text-rose-300' : 'text-rose-700';
  const tabActive = 'bg-rose-600 text-white';

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'responsibilities', label: 'Responsibilities' },
    { id: 'inputs', label: 'Intelligence' },
    { id: 'decisions', label: 'Decisions' },
    { id: 'prioritization', label: 'Prioritization' },
    { id: 'conversation', label: 'AI Conversation' },
    { id: 'autonomous', label: 'Autonomous' },
    { id: 'executive', label: 'Executive' },
    { id: 'learning', label: 'Learning' },
    { id: 'recommendations', label: 'Recommendations' },
    { id: 'ux', label: 'UX' },
    { id: 'engineering', label: 'Engineering' },
    { id: 'status', label: 'Status' },
  ];

  return (
    <div className={`space-y-6 rounded-xl p-1 ${shell}`}>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className={`text-xs font-semibold uppercase tracking-wide ${accent}`}>PRD-000009</p>
          <h1 className="text-2xl font-semibold">Marketplace Operating Brain</h1>
          <p className={`text-sm ${muted}`}>
            Autonomous marketplace intelligence, business decision engine, AI orchestration and executive advisor
          </p>
          <div className="mt-2 flex flex-wrap gap-2 text-xs">
            <Link to="/platform/growth-experiment-engine-prd" className={`${accent} hover:underline`}>PRD-000008</Link>
            <span className={muted}>→</span>
            <Link to="/platform/profit-intelligence-leakage-engine-prd" className={`${accent} hover:underline`}>PRD-000007</Link>
          </div>
        </div>
        <button type="button" onClick={() => setTheme(dark ? 'light' : 'dark')} className={`rounded-lg border px-3 py-1.5 text-sm ${card}`}>
          {dark ? 'Light' : 'Dark'}
        </button>
      </div>

      {error && <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300">{error}</div>}
      {actionMsg && <div className="rounded-lg border border-rose-300 bg-rose-50 p-3 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-950 dark:text-rose-300">{actionMsg}</div>}

      <div className="flex flex-wrap gap-2">
        {tabs.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)} className={`rounded-lg px-3 py-1.5 text-sm ${tab === t.id ? tabActive : `${card} border`}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && dashboard && (
        <div className="space-y-4">
          <div className={`rounded-xl border p-5 ${card}`}>
            <h2 className="text-lg font-semibold">Central Intelligence Layer</h2>
            <p className={`mt-1 text-sm ${muted}`}>Depends on: {dashboard.depends_on.join(' · ')}</p>
            <p className={`mt-3 text-sm ${muted}`}>
              Not to answer questions — to continuously improve seller business outcomes through coordinated,
              evidence-based and explainable decision making. Orchestrates all intelligence engines without replacing them.
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { label: 'Core Responsibilities', value: dashboard.core_responsibilities },
              { label: 'Intelligence Inputs', value: dashboard.intelligence_inputs },
              { label: 'Decision Fields', value: dashboard.decision_fields },
              { label: 'Prioritization Factors', value: dashboard.prioritization_factors },
              { label: 'Conversation Examples', value: dashboard.conversation_examples },
              { label: 'Executive Briefs', value: dashboard.executive_briefs },
              { label: 'Learning Targets', value: dashboard.learning_targets },
              { label: 'Acceptance Criteria', value: dashboard.acceptance_criteria },
            ].map((m) => (
              <div key={m.label} className={`rounded-xl border p-4 ${card}`}>
                <p className={`text-xs uppercase ${muted}`}>{m.label}</p>
                <p className="mt-1 text-2xl font-semibold">{m.value}</p>
              </div>
            ))}
          </div>
          <div className={`rounded-xl border p-5 ${card}`}>
            <h3 className="font-semibold">Business Objectives</h3>
            <div className="mt-3 grid gap-2 md:grid-cols-2 lg:grid-cols-3">
              {objectives.map((o) => (
                <div key={o.key} className="rounded-lg border border-rose-200 p-3 dark:border-rose-800">
                  <span className="font-medium">{o.name}</span>
                  <p className={`mt-1 text-xs ${muted}`}>{o.description}</p>
                  {o.kpi && <span className="mt-1 inline-block rounded bg-rose-100 px-2 py-0.5 text-xs dark:bg-rose-900">{o.kpi}</span>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === 'responsibilities' && (
        <div className="space-y-2">
          {responsibilities.sort((a, b) => a.order - b.order).map((r) => (
            <div key={r.key} className={`flex items-center gap-4 rounded-lg border px-4 py-3 ${card}`}>
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-rose-600 text-sm font-bold text-white">{r.order}</span>
              <div>
                <span className="font-medium">{r.name}</span>
                {r.description && <p className={`text-xs ${muted}`}>{r.description}</p>}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'inputs' && (
        <div className="grid gap-3 md:grid-cols-2">
          {inputs.map((i) => (
            <div key={i.key} className={`rounded-xl border p-4 ${card}`}>
              <div className="flex items-center justify-between">
                <h3 className="font-semibold">{i.name}</h3>
                {i.engine && <span className="rounded bg-fuchsia-100 px-2 py-0.5 text-xs dark:bg-fuchsia-900">{i.engine}</span>}
              </div>
              <p className={`text-xs ${accent}`}>{i.source}</p>
              {i.description && <p className={`mt-1 text-sm ${muted}`}>{i.description}</p>}
              {i.signals && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {i.signals.map((s) => <span key={s} className="rounded border px-2 py-0.5 text-xs">{s}</span>)}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === 'decisions' && (
        <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
          {decisionFields.map((f) => (
            <div key={f.key} className={`rounded-xl border p-4 ${card}`}>
              <div className="flex items-center justify-between">
                <h3 className="font-semibold">{f.name}</h3>
                {f.required && <span className="rounded bg-rose-100 px-2 py-0.5 text-xs dark:bg-rose-900">Required</span>}
              </div>
              {f.description && <p className={`mt-1 text-sm ${muted}`}>{f.description}</p>}
            </div>
          ))}
        </div>
      )}

      {tab === 'prioritization' && (
        <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
          {prioritization.map((p) => (
            <div key={p.key} className={`rounded-lg border px-4 py-3 ${card}`}>
              <span className="font-medium">{p.name}</span>
              {p.weight != null && <span className="ml-2 text-sm text-rose-600 dark:text-rose-400">{(p.weight * 100).toFixed(0)}%</span>}
              {p.description && <p className={`text-xs ${muted}`}>{p.description}</p>}
            </div>
          ))}
        </div>
      )}

      {tab === 'conversation' && (
        <div className="space-y-3">
          {conversation.map((c) => (
            <div key={c.key} className={`rounded-xl border p-4 ${card}`}>
              <p className="font-medium italic">&ldquo;{c.prompt}&rdquo;</p>
              {c.context && <p className={`mt-1 text-xs ${accent}`}>Context: {c.context}</p>}
              <div className="mt-2 flex gap-2">
                {c.requires_evidence && <span className="rounded bg-rose-100 px-2 py-0.5 text-xs dark:bg-rose-900">Evidence required</span>}
                {c.requires_confidence && <span className="rounded bg-fuchsia-100 px-2 py-0.5 text-xs dark:bg-fuchsia-900">Confidence required</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'autonomous' && (
        <div className="grid gap-3 md:grid-cols-2">
          {autonomous.map((a) => (
            <div key={a.key} className={`rounded-xl border p-4 ${card}`}>
              <div className="flex items-center justify-between">
                <h3 className="font-semibold">{a.name}</h3>
                {a.requires_approval && <span className="rounded bg-amber-100 px-2 py-0.5 text-xs dark:bg-amber-900">Approval required</span>}
              </div>
              {a.description && <p className={`mt-1 text-sm ${muted}`}>{a.description}</p>}
              {a.configurable && <span className="mt-2 inline-block rounded bg-fuchsia-100 px-2 py-0.5 text-xs dark:bg-fuchsia-900">Configurable</span>}
            </div>
          ))}
        </div>
      )}

      {tab === 'executive' && (
        <div className="grid gap-3 md:grid-cols-2">
          {executive.map((b) => (
            <div key={b.key} className={`rounded-xl border p-4 ${card}`}>
              <div className="flex items-center justify-between">
                <h3 className="font-semibold">{b.name}</h3>
                <span className="rounded bg-rose-100 px-2 py-0.5 text-xs dark:bg-rose-900">{b.schedule}</span>
              </div>
              {b.description && <p className={`mt-1 text-sm ${muted}`}>{b.description}</p>}
              {b.focus && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {b.focus.map((f) => <span key={f} className="rounded border px-2 py-0.5 text-xs">{f}</span>)}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === 'learning' && (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {learning.map((l) => (
            <div key={l.key} className={`rounded-xl border p-4 ${card}`}>
              <div className="flex items-center gap-2">
                <h3 className="font-semibold">{l.name}</h3>
                {l.versioned && <span className="rounded bg-rose-100 px-2 py-0.5 text-xs dark:bg-rose-900">Versioned</span>}
                {l.auditable && <span className="rounded bg-fuchsia-100 px-2 py-0.5 text-xs dark:bg-fuchsia-900">Auditable</span>}
              </div>
              {l.description && <p className={`mt-1 text-sm ${muted}`}>{l.description}</p>}
            </div>
          ))}
        </div>
      )}

      {tab === 'recommendations' && (
        <div className="space-y-3">
          {recommendations.map((r) => (
            <div key={r.key} className={`rounded-xl border p-5 ${card}`}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <h3 className="font-semibold">{r.title}</h3>
                <div className="flex gap-2">
                  <span className="rounded bg-rose-100 px-2 py-0.5 text-xs font-medium dark:bg-rose-900">{(r.confidence * 100).toFixed(0)}%</span>
                  <span className={`rounded px-2 py-0.5 text-xs ${riskColors[r.risk] ?? ''}`}>{r.risk} risk</span>
                </div>
              </div>
              <p className={`mt-1 text-xs ${accent}`}>{r.business_problem}</p>
              {r.root_cause && <p className={`mt-1 text-sm ${muted}`}>Root cause: {r.root_cause}</p>}
              <div className="mt-3 flex flex-wrap gap-3 text-sm">
                <span>Revenue: <strong>{r.expected_revenue}</strong></span>
                <span>Profit: <strong>{r.expected_profit}</strong></span>
                <span>Time to value: {r.time_to_value}</span>
              </div>
              {r.suggested_actions && (
                <ul className={`mt-2 list-inside list-disc text-xs ${muted}`}>
                  {r.suggested_actions.map((a) => <li key={a}>{a}</li>)}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === 'ux' && (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {uxCaps.map((u) => (
            <div key={u.key} className={`rounded-xl border p-4 ${card}`}>
              <h3 className="font-semibold">{u.name}</h3>
              {u.mode && <code className={`text-xs ${muted}`}>{u.mode}</code>}
              {u.description && <p className={`mt-1 text-sm ${muted}`}>{u.description}</p>}
            </div>
          ))}
        </div>
      )}

      {tab === 'engineering' && (
        <div className="space-y-6">
          <section>
            <h2 className="mb-3 text-lg font-semibold">Performance Targets</h2>
            <div className="grid gap-2 md:grid-cols-2">
              {perf.map((p) => (
                <div key={p.key} className={`rounded-lg border px-4 py-3 ${card}`}>
                  <span className="font-medium">{p.surface}</span>
                  <span className="ml-2 text-lg font-semibold text-rose-600 dark:text-rose-400">{p.max_value}{p.unit !== 'seconds' && p.unit !== 'enterprise' && p.unit !== 'optimized' && p.unit !== 'source_dependent' ? '' : ''}</span>
                </div>
              ))}
            </div>
          </section>
          <section>
            <h2 className="mb-3 text-lg font-semibold">APIs</h2>
            {apis.map((a) => (
              <div key={a.key} className={`mb-3 rounded-xl border p-4 ${card}`}>
                <h3 className="font-semibold capitalize">{a.category}: {a.key}</h3>
                <p className={`text-sm ${muted}`}>{a.description}</p>
                <ul className={`mt-2 list-inside list-disc text-xs ${muted}`}>
                  {a.endpoints?.map((e) => <li key={e}>{e}</li>)}
                </ul>
              </div>
            ))}
          </section>
          <section>
            <h2 className="mb-3 text-lg font-semibold">Database Schema</h2>
            <div className="grid gap-3 md:grid-cols-2">
              {tables.map((t) => (
                <div key={t.key} className={`rounded-xl border p-4 ${card}`}>
                  <h3 className="font-semibold">{t.name}</h3>
                  <p className={`text-sm ${muted}`}>{t.purpose}</p>
                </div>
              ))}
            </div>
          </section>
          <section>
            <h2 className="mb-3 text-lg font-semibold">Testing & Acceptance</h2>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                {testing.map((t) => (
                  <div key={t.key} className={`mb-2 rounded-lg border px-4 py-2 ${card}`}>
                    <span className="font-medium capitalize">{t.level}</span>
                    <span className={`ml-2 text-xs ${muted}`}>{t.scope}</span>
                  </div>
                ))}
              </div>
              <div>
                {acceptance.map((a) => (
                  <div key={a.key} className={`mb-2 rounded-lg border px-4 py-2 ${card}`}>
                    <p className="text-sm">{a.criterion}</p>
                    {a.target && <p className={`text-xs ${muted}`}>Target: {a.target}</p>}
                  </div>
                ))}
              </div>
            </div>
          </section>
        </div>
      )}

      {tab === 'status' && status && (
        <div className="space-y-4">
          <div className={`rounded-xl border p-5 ${card}`}>
            <span className="rounded-full bg-rose-100 px-3 py-1 text-sm font-medium text-rose-800 dark:bg-rose-900 dark:text-rose-200">{status.status}</span>
            {status.central_orchestration && <p className={`mt-3 text-sm ${muted}`}>Central orchestration layer — coordinates all intelligence engines</p>}
            {status.production_ready && <p className={`text-sm ${muted}`}>Production implementation specification complete</p>}
          </div>
          <div className={`rounded-xl border p-5 ${card}`}>
            <h3 className="font-semibold">Validate Marketplace Operating Brain PRD</h3>
            <button type="button" disabled={busy} onClick={validate} className={`mt-4 rounded-lg px-4 py-2 text-sm text-white ${tabActive} disabled:opacity-50`}>
              {busy ? 'Validating…' : 'Run Readiness Validation'}
            </button>
            {validation && (
              <pre className="mt-3 overflow-x-auto rounded-lg bg-slate-100 p-3 text-xs dark:bg-slate-800">{JSON.stringify(validation, null, 2)}</pre>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
